import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronRight, FiExternalLink } from 'react-icons/fi';
import { useLanguage } from '../components';
import ComplianceNotice from '../components/ComplianceNotice';
import { ACCOUNT_LEARNING_PATHS, localizeContent } from '../content/planningContent';
import './AccountsLearningCenter.css';

const UI_COPY = {
  en: {
    title: 'Canadian Registered Accounts Learning Center',
    subtitle:
      'Start with the financial decision you are facing. Then review only the account rules that matter for that situation.',
    stepPath: 'Step 1',
    stepAccount: 'Step 2',
    stepDetails: 'Step 3',
    pathTitle: 'What are you trying to understand?',
    pathSubtitle: 'Pick one situation first. You can switch paths anytime.',
    listTitle: 'Choose an account',
    listSubtitle: 'Only accounts linked to your selected situation are shown here.',
    selectedPathTitle: 'You selected',
    detailsTitle: 'Account details',
    detailsSubtitle: 'Now review the rules, limits, and examples for this account.',
    quickSummaryTitle: 'Quick summary first',
    quickSummarySubtitle: 'Read this one-screen summary, then open detailed panels below.',
    quickSummaryBestFor: 'Best use case',
    quickSummaryLimit: 'Most important limit',
    quickSummaryTax: 'Tax snapshot',
    quickSummaryRisk: 'Main mistake to avoid',
    backToStep1: 'Back to Step 1',
    backToStep2: 'Back to Step 2',
    changePath: 'Change scenario',
    changeAccount: 'Change account',
    sections: {
      keyNumbers: 'Key numbers',
      contribution: 'Contribution rules',
      tax: 'Tax treatment',
      withdrawal: 'Withdrawal and timing',
      examples: 'Worked examples',
      mistakes: 'Common mistakes to avoid',
    },
    notesTitle: 'Important note',
    notesBody:
      'Contribution limits can change annually. Confirm current values in CRA My Account before making final contribution decisions.',
    practicalGuideTitle: 'How to use this account in real life',
    practicalGuideSubtitle:
      'Use this plain-language guide to understand where this account helps most, where it can hurt, and what to check before contributing or withdrawing.',
    practicalChecklistTitle: 'Decision checklist',
    verifyTitle: 'Where to verify information',
    verifyItems: [
      'Canada Revenue Agency (CRA) pages for contribution room and tax rules.',
      'Government of Canada financial literacy resources.',
      'Your bank or brokerage account documentation.',
      'Employer pension and group savings plan documentation.',
    ],
  },
  fr: {
    title: 'Centre éducatif des comptes enregistrés canadiens',
    subtitle:
      'Commencez par la décision financière à clarifier. Consultez ensuite seulement les règles de comptes utiles dans ce contexte.',
    stepPath: 'Étape 1',
    stepAccount: 'Étape 2',
    stepDetails: 'Étape 3',
    pathTitle: 'Que voulez-vous comprendre ?',
    pathSubtitle: 'Choisissez une situation. Vous pourrez changer de parcours à tout moment.',
    listTitle: 'Choisir un compte',
    listSubtitle: 'Seuls les comptes liés à votre situation sont affichés ici.',
    selectedPathTitle: 'Parcours choisi',
    detailsTitle: 'Détails du compte',
    detailsSubtitle: 'Consultez ensuite les règles, plafonds et exemples de ce compte.',
    quickSummaryTitle: 'Résumé rapide en premier',
    quickSummarySubtitle: 'Lisez ce résumé d’un écran, puis ouvrez les panneaux détaillés ci-dessous.',
    quickSummaryBestFor: 'Meilleure utilisation',
    quickSummaryLimit: 'Plafond le plus important',
    quickSummaryTax: 'Vue fiscale rapide',
    quickSummaryRisk: 'Erreur principale à éviter',
    backToStep1: 'Retour à l’étape 1',
    backToStep2: 'Retour à l’étape 2',
    changePath: 'Changer de parcours',
    changeAccount: 'Changer de compte',
    sections: {
      keyNumbers: 'Chiffres clés',
      contribution: 'Règles de cotisation',
      tax: 'Traitement fiscal',
      withdrawal: 'Retraits et échéances',
      examples: 'Exemples chiffrés',
      mistakes: 'Erreurs courantes à éviter',
    },
    notesTitle: 'Note importante',
    notesBody:
      'Les plafonds de cotisation peuvent changer chaque année. Confirmez les valeurs actuelles dans Mon dossier ARC avant de décider.',
    practicalGuideTitle: 'Comment utiliser ce compte dans la vraie vie',
    practicalGuideSubtitle:
      'Ce guide en langage simple vous aide à voir quand ce compte est utile, les risques à éviter et ce qu’il faut vérifier avant de cotiser ou retirer.',
    practicalChecklistTitle: 'Liste de vérification',
    verifyTitle: "Où vérifier l'information",
    verifyItems: [
      "Pages de l'Agence du revenu du Canada (ARC) pour les droits de cotisation et les règles fiscales.",
      'Ressources de littératie financière du gouvernement du Canada.',
      'Documentation de votre banque ou courtier.',
      "Documentation du régime de retraite et d'épargne de votre employeur.",
    ],
  },
};

const ACCOUNTS = [
  {
    id: 'tfsa',
    code: { en: 'TFSA', fr: 'CELI' },
    name: { en: 'Tax-Free Savings Account', fr: "Compte d'epargne libre d'impot" },
    quick: {
      en: 'Flexible account for emergency funds, short-term goals, and long-term investing.',
      fr: "Compte flexible pour fonds d'urgence, projets a court terme et placements a long terme.",
    },
    overview: {
      en: 'No tax deduction on contributions, but all growth and withdrawals are generally tax-free.',
      fr: 'Aucune deduction fiscale a la cotisation, mais la croissance et les retraits sont generalement non imposables.',
    },
    keyNumbers: [
      { label: { en: 'Annual limit (2025)', fr: 'Plafond annuel (2025)' }, value: '$7,000' },
      {
        label: {
          en: 'Cumulative room since 2009 (if eligible each year)',
          fr: 'Droits cumules depuis 2009 (si admissible chaque annee)',
        },
        value: '$95,000',
      },
      { label: { en: 'Over-contribution tax', fr: 'Taxe de surcotisation' }, value: '1% per month / 1% par mois' },
    ],
    contributionRules: [
      {
        en: 'Unused room carries forward indefinitely.',
        fr: 'Les droits inutilises se reportent indefiniment.',
      },
      {
        en: 'Withdrawals create equivalent new room on January 1 of the following year.',
        fr: "Les retraits recreent des droits equivalents le 1er janvier de l'annee suivante.",
      },
      {
        en: 'Always confirm your available room in CRA My Account before large deposits.',
        fr: 'Confirmez toujours vos droits disponibles dans Mon dossier ARC avant un gros depot.',
      },
    ],
    taxRules: [
      { en: 'No deduction when you contribute.', fr: 'Aucune deduction au moment de cotiser.' },
      {
        en: 'Interest, dividends, and capital gains compound tax-free inside the account.',
        fr: 'Interets, dividendes et gains en capital croissent sans impot dans le compte.',
      },
      { en: 'Withdrawals are generally tax-free.', fr: 'Les retraits sont generalement non imposables.' },
    ],
    withdrawalRules: [
      { en: 'You can withdraw at any time.', fr: 'Vous pouvez retirer en tout temps.' },
      {
        en: 'Do not re-contribute the withdrawn amount in the same calendar year unless room exists.',
        fr: 'Ne recotisez pas le montant retire dans la meme annee civile sauf si vous avez des droits disponibles.',
      },
    ],
    examples: [
      {
        en: 'Example: $500/month = $6,000/year, which is below the $7,000 annual limit.',
        fr: 'Exemple : 500 $/mois = 6 000 $/an, donc sous le plafond annuel de 7 000 $.',
      },
      {
        en: 'If you withdraw $12,000 in July, that $12,000 room returns on January 1 of the next year.',
        fr: 'Si vous retirez 12 000 $ en juillet, ces 12 000 $ de droits reviennent le 1er janvier suivant.',
      },
    ],
    mistakes: [
      { en: 'Re-contributing too quickly after a withdrawal.', fr: 'Recotiser trop rapidement apres un retrait.' },
      { en: 'Ignoring CRA-adjusted room and creating avoidable penalties.', fr: 'Ignorer les droits ARC et creer des penalites evitables.' },
    ],
  },
  {
    id: 'rrsp',
    code: { en: 'RRSP', fr: 'REER' },
    name: { en: 'Registered Retirement Savings Plan', fr: "Regime enregistre d'epargne-retraite" },
    quick: {
      en: 'Retirement account that can reduce taxable income today and defer tax to retirement.',
      fr: "Compte retraite qui peut reduire le revenu imposable maintenant et reporter l'impot a la retraite.",
    },
    overview: {
      en: 'Best used when your current tax rate is high and expected retirement tax rate is lower.',
      fr: "Souvent optimal quand votre taux d'imposition actuel est eleve et potentiellement plus faible a la retraite.",
    },
    keyNumbers: [
      {
        label: { en: 'New room formula', fr: 'Formule des nouveaux droits' },
        value: '18% of earned income / 18% du revenu gagne',
      },
      {
        label: { en: 'Annual max new room (2025)', fr: 'Maximum annuel de nouveaux droits (2025)' },
        value: '$32,490',
      },
      {
        label: { en: 'Over-contribution tax', fr: 'Taxe de surcotisation' },
        value: '1% per month above $2,000 buffer',
      },
    ],
    contributionRules: [
      {
        en: 'Contribution room is based on previous-year earned income.',
        fr: "Les droits de cotisation dependent du revenu gagne de l'annee precedente.",
      },
      { en: 'Unused room carries forward to future years.', fr: 'Les droits inutilises se reportent aux annees futures.' },
      {
        en: 'There is a $2,000 lifetime over-contribution buffer, but excess above that is penalized monthly.',
        fr: 'Il existe un coussin a vie de 2 000 $, mais tout excedent au-dela est penalise mensuellement.',
      },
    ],
    taxRules: [
      {
        en: 'Contributions are generally deductible from taxable income.',
        fr: 'Les cotisations sont generalement deductibles du revenu imposable.',
      },
      { en: 'Growth is tax-deferred until withdrawal.', fr: "La croissance est reportee d'impot jusqu'au retrait." },
      {
        en: 'Withdrawals are taxable income (except specific programs under strict rules).',
        fr: 'Les retraits sont imposables (sauf certains programmes sous regles strictes).',
      },
    ],
    withdrawalRules: [
      {
        en: 'Withdrawals usually trigger withholding tax at source.',
        fr: "Les retraits declenchent generalement une retenue d'impot a la source.",
      },
      {
        en: 'Plan withdrawals with your annual tax bracket in mind.',
        fr: "Planifiez les retraits selon votre tranche d'imposition annuelle.",
      },
    ],
    examples: [
      {
        en: 'If earned income was $100,000, new room is $18,000 (18%), below the annual max.',
        fr: 'Si le revenu gagne est de 100 000 $, les nouveaux droits sont de 18 000 $ (18 %), sous le maximum annuel.',
      },
      {
        en: 'Contributing $10,000 can reduce taxable income by $10,000 in the contribution year.',
        fr: "Cotiser 10 000 $ peut reduire le revenu imposable de 10 000 $ l'annee de cotisation.",
      },
    ],
    mistakes: [
      { en: 'Contributing without confirming remaining room.', fr: 'Cotiser sans confirmer les droits restants.' },
      { en: 'Large withdrawals in a high-income year.', fr: 'Faire de gros retraits pendant une annee de revenu eleve.' },
    ],
  },
  {
    id: 'fhsa',
    code: { en: 'FHSA', fr: 'CELIAPP' },
    name: {
      en: 'First Home Savings Account',
      fr: "Compte d'epargne libre d'impot pour l'achat d'une premiere propriete",
    },
    quick: {
      en: 'First-home account combining RRSP-like deduction and TFSA-like qualifying withdrawals.',
      fr: 'Compte pour premiere propriete qui combine la deduction du REER et le retrait admissible non imposable du CELI.',
    },
    overview: {
      en: 'Powerful account for down-payment planning when you meet first-time home buyer rules.',
      fr: 'Compte tres efficace pour planifier la mise de fonds si vous respectez les criteres de premier acheteur.',
    },
    keyNumbers: [
      { label: { en: 'Annual contribution limit', fr: 'Plafond annuel de cotisation' }, value: '$8,000' },
      { label: { en: 'Lifetime contribution limit', fr: 'Plafond a vie de cotisation' }, value: '$40,000' },
      { label: { en: 'Unused room carry-forward cap', fr: 'Report maximal des droits inutilises' }, value: '$8,000' },
    ],
    contributionRules: [
      {
        en: 'You can carry forward unused room, but only up to $8,000.',
        fr: 'Vous pouvez reporter les droits inutilises, mais jusqu\'a 8 000 $ seulement.',
      },
      {
        en: 'You need to open the account to start accumulating carry-forward room.',
        fr: 'Vous devez ouvrir le compte pour commencer a accumuler des droits reportes.',
      },
      {
        en: 'Contributions are deductible in the contribution year.',
        fr: "Les cotisations sont deductibles dans l'annee de cotisation.",
      },
    ],
    taxRules: [
      { en: 'Deductible contributions lower taxable income.', fr: 'Les cotisations deductibles reduisent le revenu imposable.' },
      { en: 'Qualifying first-home withdrawals are tax-free.', fr: 'Les retraits admissibles pour premiere propriete sont non imposables.' },
    ],
    withdrawalRules: [
      { en: 'Non-qualifying withdrawals are taxable.', fr: 'Les retraits non admissibles sont imposables.' },
      {
        en: 'Unused FHSA balances may transfer to RRSP or RRIF under specific rules.',
        fr: 'Les soldes inutilises peuvent etre transferes au REER ou FERR sous certaines regles.',
      },
    ],
    examples: [
      {
        en: 'Contributing $8,000/year for 5 years reaches the $40,000 lifetime cap before growth.',
        fr: 'Cotiser 8 000 $/an pendant 5 ans atteint le plafond a vie de 40 000 $ avant croissance.',
      },
      {
        en: 'If you contribute $8,000 and are in a 30% tax bracket, tax savings are roughly $2,400.',
        fr: "Si vous cotisez 8 000 $ avec un taux d'imposition de 30 %, l'economie fiscale est d'environ 2 400 $.",
      },
    ],
    mistakes: [
      {
        en: 'Waiting too long to open the account and losing carry-forward potential.',
        fr: 'Attendre trop longtemps pour ouvrir le compte et perdre le potentiel de report.',
      },
      {
        en: 'Assuming all withdrawals are tax-free without qualification rules.',
        fr: 'Supposer que tous les retraits sont non imposables sans verifier les regles d\'admissibilite.',
      },
    ],
  },
  {
    id: 'resp',
    code: { en: 'RESP', fr: 'REEE' },
    name: { en: 'Registered Education Savings Plan', fr: "Regime enregistre d'epargne-etudes" },
    quick: {
      en: 'Education account that can attract government grants and long-term tax-deferred growth.',
      fr: 'Compte etudes pouvant recevoir des subventions gouvernementales et une croissance reportee d\'impot.',
    },
    overview: {
      en: 'Designed for post-secondary education savings, especially effective when started early.',
      fr: "Concu pour l'epargne postsecondaire, particulierement efficace lorsqu'il est demarre tot.",
    },
    keyNumbers: [
      {
        label: { en: 'Lifetime contribution limit', fr: 'Plafond a vie des cotisations' },
        value: '$50,000 per beneficiary',
      },
      { label: { en: 'Basic CESG grant', fr: 'Subvention SCEE de base' }, value: '20% of first $2,500 = $500/year' },
      { label: { en: 'CESG lifetime maximum', fr: 'Maximum a vie SCEE' }, value: '$7,200 per beneficiary' },
    ],
    contributionRules: [
      {
        en: 'No annual contribution cap, but grants are based on annual contribution amounts.',
        fr: 'Pas de plafond annuel de cotisation, mais les subventions dependent des cotisations annuelles.',
      },
      {
        en: 'A $5,000 contribution can generate up to $1,000 CESG in catch-up years.',
        fr: 'Une cotisation de 5 000 $ peut generer jusqu\'a 1 000 $ de SCEE lors des annees de rattrapage.',
      },
      {
        en: 'Lifetime contributions above $50,000 trigger penalties.',
        fr: 'Les cotisations a vie au-dela de 50 000 $ entrainent des penalites.',
      },
    ],
    taxRules: [
      { en: 'Contributions are not deductible.', fr: 'Les cotisations ne sont pas deductibles.' },
      {
        en: 'Investment growth and grants are taxed in the student beneficiary when paid out as educational assistance.',
        fr: "La croissance et les subventions sont imposees entre les mains de l'etudiant lors du paiement en aide aux etudes.",
      },
      {
        en: 'Original contributions can generally be withdrawn tax-free by the subscriber.',
        fr: 'Les cotisations d\'origine peuvent generalement etre retirees sans impot par le souscripteur.',
      },
    ],
    withdrawalRules: [
      {
        en: 'Withdrawals have different categories: contributions vs educational assistance payments.',
        fr: "Les retraits ont differentes categories : cotisations vs paiements d'aide aux etudes.",
      },
      {
        en: 'Plan withdrawal sequencing to maximize grants and reduce tax friction.',
        fr: 'Planifiez la sequence des retraits pour maximiser les subventions et limiter le frottement fiscal.',
      },
    ],
    examples: [
      {
        en: 'Contributing $2,500/year can capture the full basic $500 CESG each year.',
        fr: 'Cotiser 2 500 $/an permet de capter la SCEE de base complete de 500 $ chaque annee.',
      },
      {
        en: 'Contributing $50,000 over time can unlock up to $7,200 CESG plus investment growth.',
        fr: 'Cotiser 50 000 $ au fil du temps peut debloquer jusqu\'a 7 200 $ de SCEE plus la croissance des placements.',
      },
    ],
    mistakes: [
      {
        en: 'Missing grant years due to low early contributions.',
        fr: 'Perdre des annees de subvention a cause de cotisations trop faibles au debut.',
      },
      {
        en: 'Not understanding withdrawal categories before education starts.',
        fr: 'Ne pas comprendre les categories de retraits avant le debut des etudes.',
      },
    ],
  },
  {
    id: 'rrif',
    code: { en: 'RRIF', fr: 'FERR' },
    name: { en: 'Registered Retirement Income Fund', fr: 'Fonds enregistre de revenu de retraite' },
    quick: {
      en: 'Income stage account used after RRSP conversion, with mandatory minimum annual withdrawals.',
      fr: 'Compte de decaissement apres conversion du REER, avec retraits minimums annuels obligatoires.',
    },
    overview: {
      en: 'Key objective is coordinated withdrawal planning to manage taxes and retirement cash flow.',
      fr: "L'objectif principal est de coordonner les retraits pour gerer l'impot et le flux de tresorerie a la retraite.",
    },
    keyNumbers: [
      { label: { en: 'RRSP conversion deadline', fr: 'Date limite de conversion du REER' }, value: 'Dec 31 of year you turn 71' },
      {
        label: { en: 'Minimum withdrawal factor at age 71', fr: 'Facteur de retrait minimum a 71 ans' },
        value: '5.28% (e.g., $400,000 -> $21,120)',
      },
      {
        label: { en: 'Minimum withdrawal factor at age 80', fr: 'Facteur de retrait minimum a 80 ans' },
        value: '6.82% (e.g., $400,000 -> $27,280)',
      },
    ],
    contributionRules: [
      { en: 'No new contributions directly into a RRIF.', fr: 'Aucune nouvelle cotisation directe dans un FERR.' },
      {
        en: 'RRIF is funded mainly by transferring RRSP assets.',
        fr: 'Le FERR est finance principalement par transfert des actifs REER.',
      },
    ],
    taxRules: [
      { en: 'All RRIF withdrawals are taxable income.', fr: 'Tous les retraits du FERR sont imposables.' },
      {
        en: 'Amounts above the minimum are typically subject to withholding tax at source.',
        fr: "Les montants au-dessus du minimum sont generalement soumis a une retenue d'impot a la source.",
      },
    ],
    withdrawalRules: [
      {
        en: 'You must withdraw at least the annual minimum once RRIF payments begin.',
        fr: 'Vous devez retirer au moins le minimum annuel une fois les paiements FERR commences.',
      },
      {
        en: 'Coordinate RRIF withdrawals with CPP and OAS timing to reduce tax spikes.',
        fr: 'Coordonnez les retraits FERR avec le calendrier RPC et SV pour reduire les pics d\'imposition.',
      },
    ],
    examples: [
      {
        en: 'At 71, a $500,000 RRIF implies a minimum withdrawal around $26,400 (5.28%).',
        fr: 'A 71 ans, un FERR de 500 000 $ implique un retrait minimum d\'environ 26 400 $ (5,28 %).',
      },
      {
        en: 'Splitting withdrawals across years can lower total lifetime tax versus large one-time draws.',
        fr: 'Repartir les retraits sur plusieurs annees peut reduire l\'impot total a vie par rapport a de gros retraits ponctuels.',
      },
    ],
    mistakes: [
      { en: 'Ignoring required minimum withdrawals.', fr: 'Ignorer les retraits minimums obligatoires.' },
      {
        en: 'Withdrawing too much in one year and moving into a higher tax bracket.',
        fr: 'Retirer trop en une seule annee et passer a une tranche d\'imposition superieure.',
      },
    ],
  },
];

const localize = (value, lang, tt) => {
  if (typeof value === 'string') {
    return lang === 'fr' ? tt(value) : value;
  }
  if (!value || typeof value !== 'object') {
    return '';
  }
  const localized = value[lang] || value.en || '';
  return lang === 'fr' ? tt(localized) : localized;
};

const CRA_LOGIN_LINK = 'https://www.canada.ca/en/revenue-agency/services/e-services/cra-login-services.html';

const ACCOUNT_CANADA_LINKS = {
  en: {
    tfsa: 'https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/tax-free-savings-account.html',
    rrsp: 'https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/rrsps-related-plans.html',
    fhsa: 'https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/first-home-savings-account.html',
    resp: 'https://www.canada.ca/en/services/benefits/education/education-savings.html',
    rrif: 'https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/registered-retirement-income-fund-rrif.html',
  },
  fr: {
    tfsa: 'https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/celi.html',
    rrsp: 'https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/reer-regimes-connexes.html',
    fhsa: 'https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/compte-epargne-libre-impot-achat-propriete.html',
    resp: 'https://www.canada.ca/fr/services/prestations/education/etudes-epargne.html',
    rrif: 'https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/fonds-enregistre-revenu-retraite-ferr.html',
  },
};

const ACCOUNT_DETAILED_GUIDANCE = {
  tfsa: {
    en: [
      'A TFSA is usually the easiest account to start with because withdrawals are flexible and growth is generally tax-free. Many households use it as a dual-purpose account: part emergency buffer, part long-term investing.',
      'The most common planning mistake is timing. If you withdraw and then re-contribute too early in the same year, you can trigger avoidable penalties. Treat your CRA contribution room number as the final source of truth before moving large amounts.',
    ],
    fr: [
      'Le CELI est souvent le compte le plus simple pour commencer, car les retraits sont flexibles et la croissance est généralement non imposable. Beaucoup de ménages l’utilisent à la fois pour l’urgence et pour l’investissement à long terme.',
      'L’erreur la plus fréquente est le moment de recotisation. Si vous retirez puis recotisez trop vite dans la même année, vous pouvez créer des pénalités évitables. Vérifiez toujours vos droits ARC avant un gros mouvement.',
    ],
    checklist: {
      en: [
        'Confirm your current TFSA room in CRA My Account before new deposits.',
        'Separate emergency cash from long-term holdings so you do not sell investments under pressure.',
        'If you withdraw, track re-contribution timing for next calendar year room recovery.',
      ],
      fr: [
        'Confirmez vos droits CELI dans Mon dossier ARC avant toute nouvelle cotisation.',
        'Séparez l’épargne d’urgence et les placements long terme pour éviter de vendre sous pression.',
        'Après un retrait, suivez la date de recotisation pour le retour des droits au prochain 1er janvier.',
      ],
    },
  },
  rrsp: {
    en: [
      'An RRSP is mainly a tax timing tool. You receive tax relief when contributing today, then pay tax when withdrawing later. It often works best during high-income years when deductions are more valuable.',
      'The account is strongest when paired with a withdrawal plan. If you contribute aggressively but withdraw in large single-year amounts, your retirement tax bill can rise faster than expected.',
    ],
    fr: [
      'Le REER est surtout un outil de gestion du moment fiscal. Vous obtenez un allégement d’impôt à la cotisation aujourd’hui, puis l’impôt est payé au retrait plus tard. Il est souvent plus efficace pendant les années de revenu élevé.',
      'Le compte est plus puissant avec un plan de retrait. Cotiser fortement puis retirer en gros montants sur une seule année peut augmenter l’impôt à la retraite plus vite que prévu.',
    ],
    checklist: {
      en: [
        'Compare your current marginal tax rate with expected retirement tax rate.',
        'Keep a contribution log so room and deduction claims stay accurate.',
        'Plan future withdrawals by tax bracket, not only by cash need.',
      ],
      fr: [
        'Comparez votre taux d’imposition marginal actuel avec celui prévu à la retraite.',
        'Tenez un suivi des cotisations pour garder des droits et déductions exacts.',
        'Planifiez les retraits selon les tranches d’impôt, pas seulement selon le besoin de liquidités.',
      ],
    },
  },
  fhsa: {
    en: [
      'The FHSA is highly effective for first-home buyers because it combines two advantages: deductible contributions like an RRSP and qualifying withdrawals that are generally tax-free like a TFSA.',
      'Its value comes from sequence and eligibility. Opening early can protect future carry-forward room, while poor timing or non-qualifying use can reduce the benefit dramatically.',
    ],
    fr: [
      'Le CELIAPP est très efficace pour un premier achat immobilier, car il combine deux avantages: cotisations déductibles comme un REER et retraits admissibles généralement non imposables comme un CELI.',
      'Sa valeur dépend de l’ordre des actions et de l’admissibilité. Ouvrir tôt peut protéger le report des droits, alors qu’un mauvais usage peut réduire fortement l’avantage.',
    ],
    checklist: {
      en: [
        'Confirm first-home eligibility before relying on tax-free withdrawal assumptions.',
        'Open the account early if home purchase is likely within a few years.',
        'Coordinate FHSA, TFSA, and RRSP strategies instead of optimizing one account in isolation.',
      ],
      fr: [
        'Confirmez votre admissibilité de premier acheteur avant de planifier des retraits non imposables.',
        'Ouvrez le compte tôt si un achat est probable dans les prochaines années.',
        'Coordonnez CELIAPP, CELI et REER plutôt que d’optimiser un seul compte isolément.',
      ],
    },
  },
  resp: {
    en: [
      'An RESP is most powerful when used early and consistently because grants compound over time. Even moderate annual contributions can unlock meaningful government support when scheduled properly.',
      'The biggest complexity is withdrawal sequencing. Contributions, grants, and growth are not treated the same at withdrawal, so a simple plan before post-secondary start date prevents avoidable tax friction.',
    ],
    fr: [
      'Le REEE devient plus puissant lorsqu’il est utilisé tôt et régulièrement, car les subventions composent dans le temps. Même des cotisations modérées peuvent débloquer une aide importante avec une bonne cadence.',
      'La principale complexité est la séquence des retraits. Cotisations, subventions et croissance n’ont pas le même traitement, donc un plan simple avant le début des études évite du frottement fiscal.',
    ],
    checklist: {
      en: [
        'Track annual contributions to capture CESG efficiently over multiple years.',
        'Document beneficiary study timeline before first withdrawal request.',
        'Decide withdrawal order in advance to avoid grant loss or unnecessary tax.',
      ],
      fr: [
        'Suivez les cotisations annuelles pour capter la SCEE efficacement sur plusieurs années.',
        'Documentez le calendrier d’études du bénéficiaire avant le premier retrait.',
        'Définissez l’ordre des retraits à l’avance pour éviter des pertes de subvention ou d’impôt inutile.',
      ],
    },
  },
  rrif: {
    en: [
      'A RRIF is the distribution phase of retirement planning, not the accumulation phase. The core objective is to produce reliable income while controlling annual taxable withdrawals.',
      'Most tax surprises happen when withdrawals are not coordinated with CPP, OAS, and other income sources. A year-by-year withdrawal calendar is often more valuable than chasing small return differences.',
    ],
    fr: [
      'Le FERR correspond à la phase de décaissement de la retraite, pas à la phase d’accumulation. L’objectif principal est de générer un revenu stable tout en contrôlant le niveau imposable annuel.',
      'La plupart des surprises fiscales arrivent quand les retraits ne sont pas coordonnés avec le RPC, la SV et les autres revenus. Un calendrier annuel de retraits est souvent plus utile que de petits écarts de rendement.',
    ],
    checklist: {
      en: [
        'Set a withdrawal calendar that includes mandatory minimums and optional extra draws.',
        'Model income bands to reduce bracket jumps and benefit clawback risk.',
        'Review RRIF withdrawal pace every year as spending and markets change.',
      ],
      fr: [
        'Établissez un calendrier de retraits avec minimums obligatoires et retraits additionnels possibles.',
        'Modélisez les tranches de revenu pour limiter les sauts d’impôt et le risque de récupération de prestations.',
        'Révisez le rythme de retraits FERR chaque année selon vos dépenses et les marchés.',
      ],
    },
  },
};

const AccountsLearningCenter = () => {
  const { lang, tt } = useLanguage();
  const c = UI_COPY[lang] || UI_COPY.en;
  const [activePathId, setActivePathId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const accountStepRef = useRef(null);
  const detailStepRef = useRef(null);

  const localizedAccounts = useMemo(
    () =>
      ACCOUNTS.map((account) => ({
        ...account,
        code: localize(account.code, lang, tt),
        name: localize(account.name, lang, tt),
        quick: localize(account.quick, lang, tt),
        overview: localize(account.overview, lang, tt),
        keyNumbers: account.keyNumbers.map((item) => ({
          label: localize(item.label, lang, tt),
          value: item.value,
        })),
        contributionRules: account.contributionRules.map((item) => localize(item, lang, tt)),
        taxRules: account.taxRules.map((item) => localize(item, lang, tt)),
        withdrawalRules: account.withdrawalRules.map((item) => localize(item, lang, tt)),
        examples: account.examples.map((item) => localize(item, lang, tt)),
        mistakes: account.mistakes.map((item) => localize(item, lang, tt)),
      })),
    [lang, tt],
  );

  const activePath = ACCOUNT_LEARNING_PATHS.find((path) => path.id === activePathId) || null;
  const visibleAccounts = activePath
    ? localizedAccounts.filter((account) => activePath.accountIds.includes(account.id))
    : [];
  const activeAccount = activeId
    ? visibleAccounts.find((account) => account.id === activeId) || null
    : null;

  useEffect(() => {
    if (!activePath || !activeId) {
      return;
    }
    if (!activePath.accountIds.includes(activeId)) {
      setActiveId(null);
    }
  }, [activeId, activePath]);

  const activePathCopy = activePath ? localizeContent(activePath, lang) : null;
  const primaryKeyNumber = activeAccount?.keyNumbers?.[0] || null;
  const accountCanadaLink = activeAccount
    ? (ACCOUNT_CANADA_LINKS[lang] || ACCOUNT_CANADA_LINKS.en)[activeAccount.id] || null
    : null;
  const verifyItems = c.verifyItems.map((label, index) => ({
    label,
    href: index === 0 ? CRA_LOGIN_LINK : index === 1 ? accountCanadaLink : null,
  }));
  const activeGuidance = activeAccount
    ? ACCOUNT_DETAILED_GUIDANCE[activeAccount.id]
    : null;
  const guidanceParagraphs = activeGuidance
    ? (lang === 'fr' ? activeGuidance.fr : activeGuidance.en)
    : [];
  const guidanceChecklist = activeGuidance
    ? (lang === 'fr' ? activeGuidance.checklist.fr : activeGuidance.checklist.en)
    : [];

  const scrollToStep = (sectionRef) => {
    if (!sectionRef?.current || typeof window === 'undefined') {
      return;
    }
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    sectionRef.current.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  useEffect(() => {
    if (!activePath) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    const frame = window.requestAnimationFrame(() => scrollToStep(accountStepRef));
    return () => window.cancelAnimationFrame(frame);
  }, [activePath]);

  useEffect(() => {
    if (!activeAccount) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    const frame = window.requestAnimationFrame(() => scrollToStep(detailStepRef));
    return () => window.cancelAnimationFrame(frame);
  }, [activeAccount]);

  const handlePathSelect = (pathId) => {
    setActivePathId(pathId);
    setActiveId(null);
  };

  const resetToStepOne = () => {
    setActivePathId(null);
    setActiveId(null);
  };

  return (
    <div className="accounts-root">
      <section className="accounts-hero dashboard-glass">
        <h2>{c.title}</h2>
        <p>{c.subtitle}</p>
      </section>

      <ComplianceNotice compact />

      <section className="accounts-path-panel dashboard-glass">
        <div>
          <span className="accounts-step-pill">{c.stepPath}</span>
          <h3>{c.pathTitle}</h3>
          <p>{c.pathSubtitle}</p>
        </div>
        <div className="accounts-path-grid" role="tablist" aria-label={c.pathTitle}>
          {ACCOUNT_LEARNING_PATHS.map((path) => {
            const copy = localizeContent(path, lang);
            const isActive = path.id === activePathId;
            return (
              <button
                key={path.id}
                type="button"
                className={`accounts-path-choice ${isActive ? 'active' : ''}`}
                onClick={() => handlePathSelect(path.id)}
              >
                <strong>{copy.title}</strong>
                <span>{copy.description}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="accounts-learning-flow">
        {activePath && (
          <aside ref={accountStepRef} className="accounts-list-panel dashboard-glass accounts-flow-reveal">
            <span className="accounts-step-pill">{c.stepAccount}</span>
            <h3>{c.listTitle}</h3>
            <p>{c.listSubtitle}</p>
            <div className="accounts-step-actions">
              <button type="button" className="accounts-step-action-btn" onClick={resetToStepOne}>
                {c.backToStep1}
              </button>
            </div>
            <div className="accounts-selected-path">
              <small>{c.selectedPathTitle}</small>
              <strong>{activePathCopy.title}</strong>
              <span>{activePathCopy.description}</span>
            </div>
            <div className="accounts-list" role="tablist" aria-label={c.listTitle}>
              {visibleAccounts.map((account) => {
                const isActive = activeAccount?.id === account.id;
                return (
                  <button
                    key={account.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`accounts-list-item ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveId(account.id)}
                  >
                    <div className="accounts-list-head">
                      <span className="accounts-code">{account.code}</span>
                      <span className="accounts-name">{account.name}</span>
                      <FiChevronRight className="accounts-list-arrow" />
                    </div>
                    <p>{account.quick}</p>
                  </button>
                );
              })}
            </div>
          </aside>
        )}

        {activeAccount && (
          <article
            ref={detailStepRef}
            className="accounts-detail-panel dashboard-glass accounts-flow-reveal"
            role="tabpanel"
            aria-label={activeAccount.name}
          >
            <span className="accounts-step-pill">{c.stepDetails}</span>
            <header className="accounts-detail-head">
              <span className="accounts-code large">{activeAccount.code}</span>
              <div>
                <h3>{c.detailsTitle}: {activeAccount.name}</h3>
                <p>{c.detailsSubtitle}</p>
              </div>
            </header>

            <div className="accounts-step-actions">
              <button
                type="button"
                className="accounts-step-action-btn"
                onClick={() => setActiveId(null)}
              >
                {c.backToStep2}
              </button>
              <button type="button" className="accounts-step-action-btn" onClick={resetToStepOne}>
                {c.changePath}
              </button>
              <button
                type="button"
                className="accounts-step-action-btn"
                onClick={() => setActiveId(null)}
              >
                {c.changeAccount}
              </button>
            </div>

            <section className="accounts-quick-summary-panel">
              <h4>{c.quickSummaryTitle}</h4>
              <p className="accounts-quick-summary-subtitle">{c.quickSummarySubtitle}</p>
              <div className="accounts-quick-summary-grid">
                <article className="accounts-quick-summary-card">
                  <small>{c.quickSummaryBestFor}</small>
                  <p>{activeAccount.quick}</p>
                </article>
                <article className="accounts-quick-summary-card">
                  <small>{c.quickSummaryLimit}</small>
                  <p>{primaryKeyNumber ? `${primaryKeyNumber.label}: ${primaryKeyNumber.value}` : '-'}</p>
                </article>
                <article className="accounts-quick-summary-card">
                  <small>{c.quickSummaryTax}</small>
                  <p>{activeAccount.taxRules[0]}</p>
                </article>
                <article className="accounts-quick-summary-card">
                  <small>{c.quickSummaryRisk}</small>
                  <p>{activeAccount.mistakes[0]}</p>
                </article>
              </div>
            </section>

            <section className="accounts-practical-guide" aria-label={c.practicalGuideTitle}>
              <h4>{c.practicalGuideTitle}</h4>
              <p className="accounts-practical-guide-subtitle">{c.practicalGuideSubtitle}</p>
              <div className="accounts-practical-guide-body">
                {guidanceParagraphs.map((item) => (
                  <p key={`${activeAccount.id}-guide-${item}`}>{item}</p>
                ))}
              </div>
              {guidanceChecklist.length > 0 && (
                <div className="accounts-practical-checklist">
                  <h5>{c.practicalChecklistTitle}</h5>
                  <ul>
                    {guidanceChecklist.map((item) => (
                      <li key={`${activeAccount.id}-guide-check-${item}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            <section className="accounts-content-stack">
              <section className="accounts-section no-margin">
                <h4>{c.sections.keyNumbers}</h4>
                <p className="accounts-section-subtitle">{tt('Limits, thresholds, and key values to keep nearby.')}</p>
                <div className="accounts-key-grid">
                  {activeAccount.keyNumbers.map((item, index) => (
                    <div key={`${activeAccount.id}-key-${index}`} className="accounts-key-card">
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="accounts-section">
                <h4>{c.sections.contribution}</h4>
                <p className="accounts-section-subtitle">{tt('Who can contribute, how room works, and limits to monitor.')}</p>
                <ul>
                  {activeAccount.contributionRules.map((item, index) => (
                    <li key={`${activeAccount.id}-contrib-${index}`}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="accounts-section">
                <h4>{c.sections.tax}</h4>
                <p className="accounts-section-subtitle">{tt('How taxes apply when you contribute, grow, and withdraw.')}</p>
                <ul>
                  {activeAccount.taxRules.map((item, index) => (
                    <li key={`${activeAccount.id}-tax-${index}`}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="accounts-section">
                <h4>{c.sections.withdrawal}</h4>
                <p className="accounts-section-subtitle">{tt('Withdrawal timing rules and planning implications.')}</p>
                <ul>
                  {activeAccount.withdrawalRules.map((item, index) => (
                    <li key={`${activeAccount.id}-withdraw-${index}`}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="accounts-section">
                <h4>{c.sections.examples}</h4>
                <p className="accounts-section-subtitle">{tt('Simple examples using realistic Canadian account values.')}</p>
                <ul>
                  {activeAccount.examples.map((item, index) => (
                    <li key={`${activeAccount.id}-example-${index}`}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="accounts-section">
                <h4>{c.sections.mistakes}</h4>
                <p className="accounts-section-subtitle">{tt('Common errors and how to prevent them early.')}</p>
                <ul>
                  {activeAccount.mistakes.map((item, index) => (
                    <li key={`${activeAccount.id}-mistake-${index}`}>{item}</li>
                  ))}
                </ul>
              </section>
            </section>

            <section className="accounts-note">
              <h4>{c.notesTitle}</h4>
              <p>{c.notesBody}</p>
            </section>
          </article>
        )}
      </section>

      {activeAccount && (
        <section className="accounts-verify dashboard-glass">
          <h3>{c.verifyTitle}</h3>
          <ul>
            {verifyItems.map((item) => (
              <li key={item.label} className={item.href ? 'has-link' : 'no-link'}>
                {item.href && <FiExternalLink />}
                <span>
                  {item.href ? (
                    <a href={item.href} target="_blank" rel="noreferrer" className="accounts-verify-link">
                      {item.label}
                    </a>
                  ) : (
                    item.label
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default AccountsLearningCenter;
